/** Mirrors `buxus-frontend` `TrainingDocumentDto` + `trainingDocumentUtil`. */

export const TRAINING_FEATURE_SCENARIO = 'scenario'
export const TRAINING_FEATURE_TOOLBOX_TIP = 'toolboxTip'

export type TrainingFeatureTarget =
  | typeof TRAINING_FEATURE_SCENARIO
  | typeof TRAINING_FEATURE_TOOLBOX_TIP

export type TrainingListSortMode = 'newest' | 'oldest' | 'category'

export interface TrainingDocumentDto {
  id: string
  fileName: string
  title: string
  description: string | null
  objective: string | null
  jprsMet: string | null
  resourceMaterial: string | null
  iconName?: string | null
  iconBackgroundColor?: string | null
  iconColor?: string | null
  sizeBytes: number
  lastModified: string | null
  sasUrl: string
  iconUrl?: string | null
  featureType?: string | null
  publicationStatus?: string | null
  categoryName?: string | null
  categoryIconName?: string | null
  categoryIconUrl?: string | null
  categoryIconColor?: string | null
  categoryIconBackgroundColor?: string | null
  imageUrl?: string | null
  displayDate?: string | null
}

export function normalizeTrainingFeature(doc: TrainingDocumentDto): TrainingFeatureTarget {
  const raw = doc.featureType?.trim().toLowerCase()
  if (raw === 'toolboxtip') return TRAINING_FEATURE_TOOLBOX_TIP
  return TRAINING_FEATURE_SCENARIO
}

export function getTrainingFileExtension(fileName: string): string {
  const i = fileName.lastIndexOf('.')
  return i >= 0 ? fileName.slice(i + 1).toLowerCase() : ''
}

export function isTrainingBlobPdfExtension(fileName: string): boolean {
  return getTrainingFileExtension(fileName) === 'pdf'
}

export function isTrainingInlineTextOrHtmlBlob(doc: TrainingDocumentDto): boolean {
  const ext = getTrainingFileExtension(doc.fileName)
  if (ext !== 'txt' && ext !== 'html' && ext !== 'htm') return false
  const f = normalizeTrainingFeature(doc)
  return f === TRAINING_FEATURE_SCENARIO || f === TRAINING_FEATURE_TOOLBOX_TIP
}

export function getTrainingListPreviewText(doc: TrainingDocumentDto): string | null {
  if (normalizeTrainingFeature(doc) === TRAINING_FEATURE_TOOLBOX_TIP) {
    return doc.description?.trim() ? doc.description : null
  }
  return doc.objective?.trim() ? doc.objective : null
}

export function parseDisplayDateYyyyMmDdMs(raw: string | null | undefined): number | null {
  const s = raw?.trim()
  if (!s) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null
  const t = Date.UTC(y, mo - 1, d, 12, 0, 0)
  return Number.isNaN(t) ? null : t
}

export function getTrainingSortEpochMs(doc: TrainingDocumentDto): number {
  const fromMeta = parseDisplayDateYyyyMmDdMs(doc.displayDate)
  if (fromMeta !== null) return fromMeta
  return Number.NEGATIVE_INFINITY
}

function formatTrainingDateForDisplay(epochMs: number): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(epochMs))
}

export function formatTrainingListDateFromDoc(doc: TrainingDocumentDto): string | null {
  const fromMeta = parseDisplayDateYyyyMmDdMs(doc.displayDate)
  return fromMeta !== null ? formatTrainingDateForDisplay(fromMeta) : null
}

export function sortTrainingDocuments(
  docs: TrainingDocumentDto[],
  mode: TrainingListSortMode,
): TrainingDocumentDto[] {
  const copy = [...docs]
  if (mode === 'newest') {
    copy.sort((a, b) => compareByEpochMsDesc(a, b) || compareTitleCi(a, b))
    return copy
  }
  if (mode === 'oldest') {
    copy.sort((a, b) => compareByEpochMsAsc(a, b) || compareTitleCi(a, b))
    return copy
  }
  copy.sort((a, b) => {
    const cn = compareCategoryName(a.categoryName, b.categoryName)
    return cn !== 0 ? cn : compareTitleCi(a, b)
  })
  return copy
}

function compareByEpochMsDesc(a: TrainingDocumentDto, b: TrainingDocumentDto): number {
  const ta = getTrainingSortEpochMs(a)
  const tb = getTrainingSortEpochMs(b)
  if (ta !== tb) return tb < ta ? -1 : 1
  return 0
}

function compareByEpochMsAsc(a: TrainingDocumentDto, b: TrainingDocumentDto): number {
  const ta = getTrainingSortEpochMs(a)
  const tb = getTrainingSortEpochMs(b)
  if (ta !== tb) return ta < tb ? -1 : 1
  return 0
}

function compareCategoryName(a: string | null | undefined, b: string | null | undefined): number {
  const as = (a ?? '').trim()
  const bs = (b ?? '').trim()
  if (!as && bs) return 1
  if (as && !bs) return -1
  return as.localeCompare(bs, undefined, { sensitivity: 'base' })
}

function compareTitleCi(a: TrainingDocumentDto, b: TrainingDocumentDto): number {
  return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' })
}

export function formatTrainingFileBytes(bytes: number | undefined | null): string {
  if (!bytes || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function trainingDocumentKey(doc: TrainingDocumentDto): string {
  return doc.id || doc.fileName
}
